import classNames from 'clsx';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { updateGoogleAuthSdkStatus } from '../../../actions/auth';
import { oAuthSdkStates } from '../../../constants/auth/oauth';
import { desktopFuncNames } from '../../../constants/desktop';
import {
  OAUTH_GOOGLE_SIGNIN_ERROR,
  OAUTH_GOOGLE_SIGNUP_ERROR,
} from '../../../constants/localizations/auth';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import invokeDesktopFunction from '../../../utils/desktop/invokeDesktopFunction';
import isDesktop from '../../../utils/desktop/isDesktop';
import LoadingButton from '../../shared/button/LoadingButton';
import Text from '../../Text';
import css from './oauth-button.module.scss';
import { useGoogleSignIn } from './useGoogleSignIn';

// exported for testing
export const GoogleButton = ({
  actionHandler,
  buttonText,
  googleAuthSdkStatus,
  partnerParams,
  source,
  isSignIn,
  isPartner,
  onClick,
  actions,
}) => {
  const { getLocalizedText, locale } = useContext(LocationAndLocalizationContext);
  const isErrorStatus = googleAuthSdkStatus === oAuthSdkStates.error;
  const isLoadingStatus = googleAuthSdkStatus === oAuthSdkStates.loading;

  const onButtonClick = () => {
    onClick?.();
  };

  const onDesktopOverlayClick = (event) => {
    // Keep desktop flow fully custom by blocking the GIS iframe click target
    event.preventDefault();
    event.stopPropagation();

    invokeDesktopFunction(desktopFuncNames.openGoogleAuth);
    onButtonClick();
  };

  const errorText = isSignIn
    ? getLocalizedText(OAUTH_GOOGLE_SIGNIN_ERROR)
    : getLocalizedText(OAUTH_GOOGLE_SIGNUP_ERROR);

  const buttonRef = useGoogleSignIn({
    actionHandler,
    source,
    partnerParams,
    actions,
    isErrorStatus,
    locale,
    buttonOptions: { shape: isPartner ? 'default' : 'pill', click_listener: onButtonClick },
  });

  const renderButton = () => {
    if (isDesktop()) {
      return (
        <div className={css.googleButtonWrapper}>
          <div ref={buttonRef} className={css.googleButtonDesktop} />
          <button
            type="button"
            className={css.googleButtonOverlayDesktop}
            onClick={onDesktopOverlayClick}
            aria-label={getLocalizedText(buttonText)}
            data-testid="googleDesktopSignInOverlay"
          />
        </div>
      );
    }

    return <div ref={buttonRef} />;
  };

  return (
    <>
      <div
        className={classNames(css.googleButtonContainer, {
          [css.isPartnerFlow]: isPartner,
        })}
        data-testid="googleSignInOauthButton"
      >
        {isLoadingStatus ? (
          <LoadingButton
            className={classNames(css.googleButton, {
              [css.isPartnerFlow]: isPartner,
            })}
          />
        ) : (
          renderButton()
        )}
      </div>
      {isErrorStatus && <Text name={errorText} className={css.errorText} />}
    </>
  );
};

GoogleButton.propTypes = {
  actionHandler: PropTypes.func.isRequired,
  buttonText: PropTypes.string.isRequired,
  googleAuthSdkStatus: PropTypes.string.isRequired,
  actions: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  partnerParams: PropTypes.object,
  isPartner: PropTypes.bool,
  source: PropTypes.string,
};

export const mapStateToProps = (state) => ({
  googleAuthSdkStatus: get(state, 'auth.googleAuthSdkStatus'),
});

export const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({ updateGoogleAuthSdkStatus }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(GoogleButton);
